/**
 * Export the EventLog to downloadable files.
 * Spec 17 P6.2 — handler for ``/export`` and ``/export csv``.
 *
 * Two pure serializers (``toJson``, ``toCsv``) + one thin browser-
 * download trigger (``triggerDownload``). Keeping the serializers
 * pure means they're trivially unit-testable without touching the
 * DOM; the download helper is the only piece that talks to the
 * browser.
 *
 * SRP: one file, three concerns kept separate:
 *   1. toJson  — EventLog entries → pretty-printed JSON string.
 *   2. toCsv   — EventLog entries → CSV string (headered, RFC-4180
 *                quoting for commas/newlines/quotes).
 *   3. triggerDownload — create a Blob, make a URL, click an <a>,
 *                revoke the URL.
 *
 * # DECISION: CSV quotes every field, even ones that don't need it.
 * # WHY: over-quoting is harmless; forgetting-to-quote is how audit
 * #      exports silently corrupt when a writer's prompt contains a
 * #      comma or a newline. Excel handles unconditionally-quoted
 * #      fields fine.
 * # DATE: 2026-04-21
 *
 * # DECISION: JSON is the authoritative audit form; CSV is a
 * #   spreadsheet-friendly projection. When they disagree (payload
 * #   has nested structure CSV can't represent), JSON wins.
 * # WHY: payloads are arbitrary per-kind shapes (regen's diff, web's
 * #      query, etc.). Forcing them into CSV would lose structure;
 * #      stringifying the payload to a JSON cell keeps the data
 * #      roundtrip-able while still being openable in Excel.
 * # DATE: 2026-04-21
 */

import type { EventLogEntry } from "../eventLog";

// ---------------------------------------------------------------------------
// Pure serializers
// ---------------------------------------------------------------------------

/** Pretty-printed JSON of the entries. Caller decides whether the
 *  entries are filtered, reversed, etc. — toJson is agnostic. */
export function toJson(entries: readonly EventLogEntry[]): string {
  return `${JSON.stringify(entries, null, 2)}\n`;
}

const CSV_HEADERS = ["id", "ts", "iso_ts", "actor", "kind", "section_id", "run_id", "payload"] as const;

/**
 * CSV form — one row per entry. Headers match the EventLogEntry
 * shape; ``payload`` is the kind-specific blob stringified as JSON
 * in a single cell so the row stays flat.
 */
export function toCsv(entries: readonly EventLogEntry[]): string {
  const lines: string[] = [CSV_HEADERS.map(csvEscape).join(",")];
  for (const e of entries) {
    lines.push(
      [
        e.id,
        String(e.ts),
        new Date(e.ts).toISOString(),
        e.actor,
        e.kind,
        e.sectionId ?? "",
        e.runId ?? "",
        JSON.stringify(e.payload),
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/**
 * Always-quote CSV escaping. Doubles inner quotes per RFC-4180.
 * Excel-compatible. Safe for any string including commas, newlines,
 * and embedded quotes.
 */
function csvEscape(value: string): string {
  const safe = value.replace(/"/g, '""');
  return `"${safe}"`;
}

// ---------------------------------------------------------------------------
// Browser download
// ---------------------------------------------------------------------------

/**
 * Trigger a file download via Blob + anchor click. Returns true when
 * the download was initiated; false when the browser environment is
 * missing (SSR, tests without jsdom, etc.) — callers can report the
 * failure without crashing.
 *
 * Thin wrapper so the call site in ``VariantCShell.handleCommand``
 * stays a one-liner and the serialisation vs transport concerns are
 * testable separately.
 */
export function triggerDownload(filename: string, mime: string, content: string): boolean {
  if (typeof document === "undefined" || typeof URL === "undefined") {
    return false;
  }
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Append + click + remove keeps Firefox happy — some versions won't
  // trigger the download if the anchor isn't in the document tree.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick so the download has picked up the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}

// ---------------------------------------------------------------------------
// Filename helper — stable format "asthra-timeline-<runId>-<iso>.<ext>"
// ---------------------------------------------------------------------------

/**
 * Canonical export filename. Sortable when stacked in a Downloads
 * folder because the timestamp is ISO-formatted with dashes (safe for
 * every major filesystem). Omits ``runId`` when absent.
 */
export function exportFilename(ext: "json" | "csv", runId: string | null): string {
  const iso = new Date().toISOString().replace(/[:.]/g, "-").replace(/T/, "_").replace(/Z$/, "");
  const runPart = runId ? `-${runId}` : "";
  return `asthra-timeline${runPart}-${iso}.${ext}`;
}
